import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, Clock, MapPin, Plus, ChevronDown, ChevronUp, List, Grid, Trash2 } from 'lucide-react';
import { usePlayer } from '../../context/PlayerContext';
import { useSeason } from '../../context/SeasonContext';
import GameScheduleItem from './GameScheduleItem';
import AddGameScheduleModal from './AddGameScheduleModal';
import MatchupAnalysis from './MatchupAnalysis';
import PreGamePrep from './PreGamePrep';
import CalendarView from './CalendarView';
import StatCard from '../shared/StatCard';
import WeatherCard from '../shared/WeatherCard';
import HeaderWithSeasonSelector from '../shared/HeaderWithSeasonSelector';
const SchedulePage = () => {
  const { playerData, addScheduledGame, removeScheduledGame, updateScheduledGameData, fetchScheduledGames, isLoading, error } = usePlayer();
  const { seasons, addSeason, activeSeason } = useSeason();
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('upcoming'); // 'upcoming', 'past', 'all'
  const [seasonFilter, setSeasonFilter] = useState(activeSeason ? activeSeason.id : '');
  const [leagueFilter, setLeagueFilter] = useState('');
  const [showMatchupAnalysis, setShowMatchupAnalysis] = useState(false);
  const [showPreGamePrep, setShowPreGamePrep] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [viewMode, setViewMode] = useState('list'); // 'calendar', 'list'
  const [selectedGame, setSelectedGame] = useState(null);
  
  // Helper function to get UTC date string (yyyy-mm-dd) to avoid timezone issues
  const getUTCDateString = (date) => {
    return `${date.getUTCFullYear()}-${(date.getUTCMonth() + 1).toString().padStart(2, '0')}-${date.getUTCDate().toString().padStart(2, '0')}`;
  };
  
  // Add debug logs
  console.log('Player Data:', playerData);
  console.log('Scheduled Games:', playerData.scheduledGames);
  
  // Check if a game is in the past or upcoming
  const determineGameStatus = useCallback((game) => {
    // Always respect the explicit status if it's already defined
    // This ensures we don't recalculate status for games with known state
    if (game.status) {
      console.log(`Game using explicit status: ${game.status} for game vs ${game.opponent} on ${game.date}`);
      return game.status;
    }
    
    try {
      // Parse the game date with detailed logging
      console.log(`Original game date string: "${game.date}" (type: ${typeof game.date})`);
      
      // Create a date object with noon time to avoid timezone issues
      let gameDate;
      if (typeof game.date === 'string') {
        // For YYYY-MM-DD format (from date input)
        if (game.date.includes('-')) {
          gameDate = new Date(game.date + 'T12:00:00');
        } 
        // For MM/DD/YYYY format
        else if (game.date.includes('/')) {
          const [month, day, year] = game.date.split('/');
          gameDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T12:00:00`);
        } 
        // Try to parse as is with noon time
        else {
          gameDate = new Date(game.date);
          // If parsing worked, set the time to noon
          if (!isNaN(gameDate.getTime())) {
            gameDate.setHours(12, 0, 0, 0);
          }
        }
      } else {
        // If it's already a Date object
        gameDate = new Date(game.date);
        // Set time to noon
        gameDate.setHours(12, 0, 0, 0);
      }
      
      const today = new Date();
      
      // Simple comparison - if game date is before today, it's in the past
      const isGameInPast = gameDate < today;
      
      // Detailed logging
      console.log('Game date comparison:', { 
        originalGameDate: game.date,
        parsedGameDate: gameDate.toString(),
        today: today.toString(),
        isGameInPast,
        calculatedStatus: isGameInPast ? 'past' : 'upcoming'
      });
      
      // Calculate new status
      const newStatus = isGameInPast ? 'past' : 'upcoming';
      
      // Log status calculation result
      console.log(`Game "${game.opponent}" on ${game.date} status determined as: ${newStatus}`);
      
      // Return status based on comparison
      return newStatus;
    } catch (error) {
      console.error('Error determining game status for date: ', game.date, error);
      // Default to upcoming if there's an error
      return 'upcoming';
    }
  }, []);

  // Handle adding a new scheduled game
  // Effect to automatically update game status based on current date
  useEffect(() => {
    const updateGameStatuses = async () => {
      console.log('Running updateGameStatuses, scheduledGames:', playerData.scheduledGames);
      if (!playerData.scheduledGames || playerData.scheduledGames.length === 0) return;
      
      // Get current date
      const today = new Date();
      
      // Find games that need to be updated from upcoming to past
      const gamesNeedingUpdate = playerData.scheduledGames.filter(game => {
        // Create a date object with noon time to avoid timezone issues
        const gameDate = new Date(game.date + 'T12:00:00');
        
        const isPastGame = gameDate < today;
        const needsUpdate = isPastGame && game.status === 'upcoming';
        
        console.log(`Game vs ${game.opponent} on ${game.date}: `, {
          gameDate: gameDate.toString(),
          today: today.toString(),
          isPastGame,
          currentStatus: game.status,
          needsStatusUpdate: needsUpdate
        });
        
        return needsUpdate;
      });
      
      console.log(`Found ${gamesNeedingUpdate.length} games that need status update to 'past'`);
      
      // Update each game that has passed
      for (const game of gamesNeedingUpdate) {
        try {
          console.log(`Updating game vs ${game.opponent} on ${game.date} from 'upcoming' to 'past'`);
          await updateScheduledGameData(game.id, { ...game, status: 'past' });
        } catch (err) {
          console.error('Error updating game status:', err);
        }
      }
      
      if (gamesNeedingUpdate.length > 0) {
        // Refresh the games list after updates
        fetchScheduledGames();
      }
    };
    
    updateGameStatuses();
  }, [playerData.scheduledGames, updateScheduledGameData, fetchScheduledGames]);

  // Update seasonFilter when activeSeason changes
  useEffect(() => {
    if (activeSeason) {
      setSeasonFilter(activeSeason.id);
    } else {
      setSeasonFilter('');
    }
  }, [activeSeason]);

  // Fetch scheduled games when component mounts
  useEffect(() => {
    // Fetch scheduled games when component mounts
    fetchScheduledGames();
    
    // Log all games and their statuses for debugging
    if (playerData.scheduledGames && playerData.scheduledGames.length > 0) {
      console.log("All scheduled games with statuses:");
      playerData.scheduledGames.forEach(game => {
        const status = determineGameStatus(game);
        console.log(`Game vs ${game.opponent} on ${game.date}: status = ${status}, stored status = ${game.status || 'none'}`);
      });
    } else {
      console.log("No scheduled games found");
    }
  }, [fetchScheduledGames, playerData.scheduledGames, determineGameStatus]); // Add proper dependency
  const handleAddGame = async (newGame) => {
    try {
      // Show loading feedback
      setFeedback({ message: 'Adding game...', type: 'loading' });
      
      // Create date objects for comparison only
      // Use the date string directly from the input (YYYY-MM-DD format)
      const inputDate = newGame.date; // Keep the original input date string
      
      // For comparison purposes only (to determine if game is past or upcoming)
      const gameDate = new Date(inputDate + 'T12:00:00'); // Add noon time to avoid timezone issues
      const today = new Date();
      
      // Set game status based on simple date comparison
      const gameStatus = gameDate < today ? 'past' : 'upcoming';
      
      console.log('Adding new game:', {
        ...newGame,
        status: gameStatus,
        date: inputDate,
        isInPast: gameDate < today
      });
      
      // Save the game with the correct status and the original input date
      await addScheduledGame({
        ...newGame,
        status: gameStatus,
        date: inputDate // Use the original date string from the input
      });
      
      setShowAddModal(false);
      setFeedback({ message: 'Game added successfully!', type: 'success' });
      
      // Clear success message after 3 seconds
      setTimeout(() => setFeedback({ message: '', type: '' }), 3000);
    } catch (err) {
      console.error('Error adding game:', err);
      setFeedback({ message: 'Failed to add game. Please try again.', type: 'error' });
    }
  };
  
  // Handle deleting a scheduled game
  const handleDeleteGame = async (gameId) => {
    try {
      setFeedback({ message: 'Deleting game...', type: 'loading' });
      
      await removeScheduledGame(gameId);
      
      setFeedback({ message: 'Game deleted successfully!', type: 'success' });
      
      // Clear success message after 3 seconds
      setTimeout(() => setFeedback({ message: '', type: '' }), 3000);
    } catch (err) {
      console.error('Error deleting game:', err);
      setFeedback({ message: 'Failed to delete game. Please try again.', type: 'error' });
    }
  };
  const filteredGames = useMemo(() => {
    if (!playerData.scheduledGames) return [];
    
    // Create a map to track unique games by a composite key
    const uniqueGames = new Map();
    
    // First pass: filter games based on criteria
    const filtered = playerData.scheduledGames.filter(game => {
      // Filter by game status (past, upcoming, all)
      const status = determineGameStatus(game);
      const statusMatch = filter === 'all' || status === filter;
      
      // Filter by season if a season filter is selected
      const seasonMatch = !seasonFilter || game.seasonId === seasonFilter;
      
      // Filter by league if a league filter is selected
      const leagueMatch = !leagueFilter || game.league === leagueFilter;
      
      // Game must match all active filters
      return statusMatch && seasonMatch && leagueMatch;
    });
    
    // Second pass: remove duplicates
    filtered.forEach(game => {
      // Create a unique key based on date, opponent, and location
      const key = `${game.date}-${game.opponent}-${game.location || ''}`;
      
      // If we haven't seen this game before, or if this instance has a seasonId and the stored one doesn't,
      // store it in our map (prefer games with seasonId)
      if (!uniqueGames.has(key) || 
          (game.seasonId && !uniqueGames.get(key).seasonId)) {
        uniqueGames.set(key, game);
      }
    });
    
    // Convert map values back to array
    return Array.from(uniqueGames.values());
  }, [playerData.scheduledGames, filter, seasonFilter, leagueFilter, determineGameStatus]);
  
  console.log('Filtered Games:', filteredGames);
  
  // Find the next upcoming game - sort by date first to ensure we get the chronologically next game
  const nextGame = playerData.scheduledGames ? [...playerData.scheduledGames]
    .filter(game => determineGameStatus(game) === 'upcoming')
    .sort((a, b) => {
      // Create date objects for comparison
      const dateA = new Date(a.date + 'T12:00:00');
      const dateB = new Date(b.date + 'T12:00:00');
      return dateA - dateB;
    })[0] : null;
  
  // Add loading state handling
  if (isLoading && !playerData.scheduledGames) {
    return (
      <div>
        <HeaderWithSeasonSelector
          title="Game Schedule"
          subtitle="Track your upcoming games"
        />
      <div className="p-4 pb-24">
          <div className="text-center py-6 text-gray-500">Loading games...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderWithSeasonSelector
        title="Game Schedule"
        subtitle="Track your upcoming games"
        actions={[
          {
            icon: Plus,
            onClick: () => setShowAddModal(true),
            label: 'Add Game'
          }
        ]}
      />
      
      <div className="p-4 pb-24">
        <div className="mb-4">
          {/* View mode toggle */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              <button 
                className={`${filter === 'upcoming' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'} px-3 py-1 rounded-full text-sm whitespace-nowrap`}
                onClick={() => setFilter('upcoming')}
              >
                Upcoming
              </button>
              <button 
                className={`${filter === 'past' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'} px-3 py-1 rounded-full text-sm whitespace-nowrap`}
                onClick={() => setFilter('past')}
              >
                Past
              </button>
              <button 
                className={`${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'} px-3 py-1 rounded-full text-sm whitespace-nowrap`}
                onClick={() => setFilter('all')}
              >
                All Games
              </button>
            </div>
            
            <div className="flex space-x-2">
              <button
                className={`p-2 rounded ${viewMode === 'calendar' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                onClick={() => setViewMode('calendar')}
                title="Calendar View"
              >
                <Grid size={18} />
              </button>
              <button
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>
          </div>
          
          {/* Season and League Filters */}
          <div className="mt-2 flex flex-wrap gap-2">
            <select 
              className="px-3 py-1 rounded-md text-sm border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary bg-white"
              value={seasonFilter}
              onChange={(e) => {
                setSeasonFilter(e.target.value);
              }}
            >
              <option value="">All Seasons</option>
              {playerData.seasons && Object.keys(playerData.seasons || {}).length > 0 ? (
                Object.keys(playerData.seasons).map((season, index) => (
                  <option key={index} value={season}>{season}</option>
                ))
              ) : (
                <option value="2024">2024</option>
              )}
            </select>
            
            <select 
              className="px-3 py-1 rounded-md text-sm border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary bg-white"
              value={leagueFilter}
              onChange={(e) => {
                setLeagueFilter(e.target.value);
              }}
            >
              <option value="">All Leagues</option>
              {playerData.leagues && playerData.leagues.length > 0 ? (
                playerData.leagues.map((league, index) => (
                  <option key={index} value={league}>{league}</option>
                ))
              ) : (
                <option value="Default League">Default League</option>
              )}
            </select>
          </div>
        </div>
      
      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarView 
          onGameSelect={(game) => setSelectedGame(game)} 
          onDeleteGame={handleDeleteGame}
          games={filteredGames}
        />
      )}
      
      {/* Next Game Summary (only shown if upcoming games exist and in list view or if a game is selected) */}
      {((viewMode === 'list' && filter !== 'past' && nextGame) || selectedGame) && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-medium">{selectedGame ? 'Selected Game' : 'Next Game'}</h3>
            <div className="flex space-x-2">
              <button 
                onClick={() => setShowPreGamePrep(!showPreGamePrep)}
                className="text-sm text-blue-600 flex items-center"
              >
                {showPreGamePrep ? 'Hide Prep' : 'Game Prep'}
                {showPreGamePrep ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
              </button>
              <button 
                onClick={() => setShowMatchupAnalysis(!showMatchupAnalysis)}
                className="text-sm text-blue-600 flex items-center"
              >
                {showMatchupAnalysis ? 'Hide Analysis' : 'Analysis'}
                {showMatchupAnalysis ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
              </button>
            </div>
          </div>
          
          <StatCard>
            <div className="flex justify-between mb-2">
              <div className="font-bold text-lg">vs {selectedGame ? selectedGame.opponent : nextGame.opponent}</div>
              <div className={`px-2 py-1 rounded text-xs ${
                (selectedGame ? selectedGame.isHome : nextGame.isHome) 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {(selectedGame ? selectedGame.isHome : nextGame.isHome) ? 'HOME' : 'AWAY'}
              </div>
            </div>
            
            <div className="space-y-2 mb-3">
              <div className="flex items-center text-gray-600">
                <Calendar size={16} className="mr-2" />
                <span>
                  {new Date((selectedGame ? selectedGame.date : nextGame.date) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </span>
              </div>
              <div className="flex items-center text-gray-600">
                <Clock size={16} className="mr-2" />
                <span>{selectedGame ? selectedGame.time : nextGame.time}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin size={16} className="mr-2" />
                <span>{selectedGame ? selectedGame.location : nextGame.location}</span>
              </div>
            </div>
            
            {/* Weather Preview */}
            <WeatherCard 
              location={selectedGame ? selectedGame.location : nextGame.location}
              date={selectedGame ? selectedGame.date : nextGame.date}
              gameTime={selectedGame ? selectedGame.time : nextGame.time}
            />
            
            {/* Action buttons for the next game */}
            <div className="flex justify-between mt-4 pt-2 border-t border-gray-100">
              <div className="flex space-x-3">
                <button 
                  className="text-blue-600 text-sm flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Add to calendar functionality
                  }}
                >
                  <Calendar size={14} className="mr-1" />
                  Add to Calendar
                </button>
                <button 
                  className="text-blue-600 text-sm flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Get directions functionality
                  }}
                >
                  <MapPin size={14} className="mr-1" />
                  Get Directions
                </button>
              </div>
              <div className="flex space-x-3">
                <button 
                  className="text-green-600 text-sm flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Open add stats modal
                    // This would typically navigate to the add game form with this game pre-populated
                    window.location.href = `/add-game?gameId=${selectedGame ? selectedGame.id : nextGame.id}`;
                  }}
                >
                  <Plus size={14} className="mr-1" />
                  Add Stats
                </button>
                <button 
                  className="text-red-600 text-sm flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedGame) {
                      handleDeleteGame(selectedGame.id);
                    } else if (nextGame) {
                      handleDeleteGame(nextGame.id);
                    }
                  }}
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete Game
                </button>
              </div>
            </div>
          </StatCard>
          
          {/* Pre-Game Preparation */}
          {showPreGamePrep && (
            <PreGamePrep 
              gameDate={selectedGame ? selectedGame.date : nextGame.date}
              opponent={selectedGame ? selectedGame.opponent : nextGame.opponent}
              gameTime={selectedGame ? selectedGame.time : nextGame.time}
            />
          )}
          
          {/* Matchup Analysis */}
          {showMatchupAnalysis && (
            <MatchupAnalysis 
              opponent={selectedGame ? selectedGame.opponent : nextGame.opponent} 
              gameDate={selectedGame ? selectedGame.date : nextGame.date}
            />
          )}
        </div>
      )}
      
      {/* Schedule List (only shown in list view) */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {filteredGames.length > 0 ? (
            filteredGames.map(game => (
              <GameScheduleItem 
                key={game.id} 
                game={game} 
                onDelete={handleDeleteGame}
                onClick={() => setSelectedGame(game)}
                isSelected={selectedGame && selectedGame.id === game.id}
              />
            ))
          ) : (
            <div className="text-center py-6 text-gray-500">
              No {filter} games scheduled
            </div>
          )}
        </div>
      )}
      
      {showAddModal && (
        <AddGameScheduleModal 
          onClose={() => setShowAddModal(false)} 
          onAdd={handleAddGame} 
          leagues={playerData.leagues || []} 
          seasons={seasons}
          onAddSeason={addSeason}
        />
      )}
      
      {/* Loading and Error Messages */}
      {isLoading && !feedback.message && (
        <div className="fixed bottom-4 right-4 bg-blue-100 text-blue-800 p-3 rounded shadow">
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        </div>
      )}
      
      {feedback.message && (
        <div className={`fixed bottom-4 right-4 p-3 rounded shadow ${
          feedback.type === 'error' ? 'bg-red-100 text-red-800' : 
          feedback.type === 'success' ? 'bg-green-100 text-green-800' : 
          'bg-blue-100 text-blue-800'}`}>
          {feedback.type === 'loading' && (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          )}
          {feedback.message}
        </div>
      )}
      
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 text-red-800 p-3 rounded shadow">
          {error}
        </div>
      )}
      </div>
    </div>
  );
};

export default SchedulePage;
